import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, RefreshCw, ArrowRight, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function Verify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // "loading" | "success" | "error"
  const [message, setMessage] = useState("Verifying your ExpoLead account...");
  const [countdown, setCountdown] = useState(4);
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    const token = searchParams.get("token");

    if (!token) {
      setState("error");
      setMessage("Missing verification token. Please use the link sent to your email.");
      return;
    }

    api
      .get(`/auth/verify?token=${encodeURIComponent(token)}`)
      .then((res) => {
        if (!isMountedRef.current) return;
        setState("success");
        setMessage(res?.data?.message || "Account verified successfully! You can now log in.");
      })
      .catch((err) => {
        if (!isMountedRef.current) return;
        setState("error");
        const errMsg = err?.response?.data?.detail || "Invalid or expired verification link. Please request a new one.";
        setMessage(errMsg);
      });

    return () => {
      isMountedRef.current = false;
    };
  }, [searchParams]);

  // Countdown auto-redirect on success
  useEffect(() => {
    if (state !== "success") return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/auth/login");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [state, navigate]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail || !resendEmail.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }

    try {
      setResending(true);
      const res = await api.post("/auth/resend-verification", { email: resendEmail.trim().toLowerCase() });
      toast.success(res?.data?.message || "Verification link sent! Please check your inbox.");
      setResendEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resend verification email.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col justify-center items-center px-4 py-12">
      <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl p-8 shadow-2xl space-y-6 text-center">
        {/* BRAND LOGO */}
        <div className="flex justify-center items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-black tracking-tight text-white">
            Expo<span className="text-blue-500">Lead</span>
          </span>
        </div>

        {/* LOADING STATE */}
        {state === "loading" && (
          <div className="py-8 space-y-4">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
              <RefreshCw className="w-7 h-7 text-blue-400 animate-spin" />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-bold text-white">Verifying Account…</h2>
              <p className="text-xs text-slate-400">Please wait while we confirm your email address.</p>
            </div>
          </div>
        )}

        {/* SUCCESS STATE */}
        {state === "success" && (
          <div className="py-4 space-y-5 animate-in fade-in zoom-in-95 duration-300">
            <div className="w-16 h-16 mx-auto rounded-3xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shadow-lg shadow-emerald-500/10">
              <CheckCircle2 className="w-9 h-9 text-emerald-400" />
            </div>

            <div className="space-y-2">
              <h2 className="text-xl font-black text-white">Account Verified! 🎉</h2>
              <p className="text-xs text-slate-300 leading-relaxed max-w-sm mx-auto">
                {message}
              </p>
              <p className="text-[11px] text-slate-400 font-medium">
                Redirecting to login in <strong className="text-blue-400 font-bold">{countdown}s</strong>…
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate("/auth/login")}
              className="w-full py-3.5 px-4 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-xl transition-all shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Login to Your Account</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* ERROR STATE */}
        {state === "error" && (
          <div className="py-4 space-y-5 animate-in fade-in zoom-in-95 duration-300">
            <div className="w-16 h-16 mx-auto rounded-3xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center shadow-lg shadow-rose-500/10">
              <AlertTriangle className="w-9 h-9 text-rose-400" />
            </div>

            <div className="space-y-2">
              <h2 className="text-lg font-black text-white">Verification Failed</h2>
              <p className="text-xs text-slate-300 leading-relaxed max-w-sm mx-auto">
                {message}
              </p>
            </div>

            {/* RESEND FORM */}
            <form onSubmit={handleResend} className="space-y-3 pt-2 text-left">
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                Request a new verification link
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-500 absolute left-3.5 top-3.5" />
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="Enter your registered email"
                  required
                  className="w-full pl-10 pr-3.5 py-2.5 bg-slate-950/70 border border-slate-800 focus:border-blue-500 rounded-xl text-xs text-white placeholder-slate-500 outline-none transition"
                />
              </div>

              <button
                type="submit"
                disabled={resending}
                className="w-full py-2.5 px-4 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs rounded-xl transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {resending && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                <span>Resend Verification Email</span>
              </button>
            </form>

            <div className="pt-2 border-t border-slate-800/80">
              <Link
                to="/auth/login"
                className="text-xs font-bold text-blue-400 hover:text-blue-300 transition"
              >
                Already verified? Back to Login
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}